import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type IUser } from '@/types/IUser';
import { uploadAvatarThunk, deleteAvatarThunk } from './accountThunks';

interface AccountState {
  profile: IUser | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
}

const initialState: AccountState = {
  profile: null,
  isLoading: false,
  error: null,
  lastUpdated: null
};

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setAccountProfile(state, action: PayloadAction<IUser | null>) {
      state.profile = action.payload;
      state.lastUpdated = action.payload !== null ? Date.now() : null;

      // Синхронизация с localStorage
      if (action.payload !== null) {
        localStorage.setItem('accountProfile', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('accountProfile');
      }
    },
    clearAccountData(state) {
      state.profile = null;
      state.lastUpdated = null;
      state.error = null;
      state.isLoading = false;

      // Очищаем localStorage
      localStorage.removeItem('accountProfile');
    },
    updateAccountField(state, action: PayloadAction<Partial<IUser>>) {
      if (state.profile !== null) {
        state.profile = { ...state.profile, ...action.payload };
        state.lastUpdated = Date.now();

        // Синхронизация с localStorage
        localStorage.setItem('accountProfile', JSON.stringify(state.profile));
      }
    },
    clearError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Загрузка аватара
    builder
      .addCase(uploadAvatarThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(uploadAvatarThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
        state.lastUpdated = Date.now();
        state.error = null;

        // Синхронизация с localStorage
        localStorage.setItem('accountProfile', JSON.stringify(action.payload));
      })
      .addCase(uploadAvatarThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload === undefined
            ? null
            : (action.payload?.message ??
              'Произошла ошибка при загрузке аватара');
      });

    // Удаление аватара
    builder
      .addCase(deleteAvatarThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAvatarThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
        state.lastUpdated = Date.now();
        state.error = null;

        // Синхронизация с localStorage
        localStorage.setItem('accountProfile', JSON.stringify(action.payload));
      })
      .addCase(deleteAvatarThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload === undefined
            ? null
            : (action.payload?.message ??
              'Произошла ошибка при удалении аватара');
      });
  }
});

export const {
  setAccountProfile,
  clearAccountData,
  updateAccountField,
  clearError
} = accountSlice.actions;

export { uploadAvatarThunk, deleteAvatarThunk } from './accountThunks';

export default accountSlice.reducer;
